/** Normalize API / UI appointment status strings for comparisons. */
export function normalizeAppointmentStatus(status: string | null | undefined): string {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
}

const TERMINAL_APPOINTMENT_STATUSES = new Set([
  'completed',
  'complete',
  'done',
  'finished',
  'closed',
  'cancelled',
  'canceled',
]);

/** True when the API marks the session as finished (not merely in the past). */
export function isTerminalAppointmentStatus(status: string | null | undefined): boolean {
  const s = normalizeAppointmentStatus(status);
  if (!s) return false;
  if (TERMINAL_APPOINTMENT_STATUSES.has(s)) return true;
  return s.endsWith('_completed') || s.startsWith('completed_');
}

function resolveTimeMs(value: Date | number | string | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

export type AppointmentActionTimeFallback = {
  startAt: Date;
  durationMinutes?: number | null;
};

/**
 * When true, hide Join, Reschedule, and Cancel controls — the session is finished.
 * Uses terminal statuses (`completed`, `complete`, common aliases) and any session
 * whose end time is already in the past (covers "past" list rows that still say `active`).
 * `pending` rows (e.g. reschedule requests) are not hidden by the time rule alone.
 */
export function shouldHideAppointmentJoinRescheduleCancel(
  status: string | null | undefined,
  sessionEndAt?: Date | number | string | null,
  timeFallback?: AppointmentActionTimeFallback,
): boolean {
  if (isTerminalAppointmentStatus(status)) {
    return true;
  }

  const s = normalizeAppointmentStatus(status);

  if (s === 'pending') {
    return false;
  }

  let endMs = resolveTimeMs(sessionEndAt);
  if (endMs == null && timeFallback?.startAt) {
    const dur = timeFallback.durationMinutes;
    if (typeof dur === 'number' && dur > 0) {
      endMs = timeFallback.startAt.getTime() + dur * 60_000;
    }
  }

  if (endMs != null && endMs < Date.now()) {
    return true;
  }

  return false;
}

export type CanJoinAppointmentOptions = {
  /** Minutes before start when Join is shown (default 30). */
  joinWindowMinutes?: number;
};

/** Dashboard Today tab: show Join Now when the session starts within this many minutes. */
export const DASHBOARD_JOIN_WINDOW_MINUTES = 60;

/**
 * Returns true when the session is starting within `joinWindowMinutes` (default 30)
 * or has already started but has not ended yet.
 */
export function canJoinAppointment(
  status: string | null | undefined,
  sessionStartAt: Date | number | string | null,
  sessionEndAt?: Date | number | string | null,
  timeFallback?: AppointmentActionTimeFallback,
  options?: CanJoinAppointmentOptions,
): boolean {
  if (shouldHideAppointmentJoinRescheduleCancel(status, sessionEndAt, timeFallback)) {
    return false;
  }

  const startMs = resolveTimeMs(sessionStartAt) ?? (timeFallback ? timeFallback.startAt.getTime() : null);
  if (startMs == null) return false;

  const now = Date.now();
  const windowMinutes = options?.joinWindowMinutes ?? 30;
  const windowMs = windowMinutes * 60 * 1000;

  return now >= startMs - windowMs;
}
